<template>
  <div class="modal-mask" ref="modalMask" :style="modalStyle">
    <div class="modal-wrapper" ref="modalWrapper" @click.self="handleClickOutside">
      <div class="modal-container" ref="modalContainer">
        <div class="modal-header" ref="modalHeader" v-if="$slots.header || closeBtn">
          <div class="modal-title" ref="modalTitle">
            <slot name="header"> </slot>
          </div>
          <a
            v-if="closeBtn"
            href=""
            class="close-btn"
            :class="{ closeBtnHidden }"
            aria-label="Close"
            @click.prevent="$emit('close')"
          >
            <img src="@/assets/icons/modal-close.svg" alt="Close modal button" />
          </a>
        </div>
        <div class="modal-body" ref="modalBody" v-bind="$attrs">
          <slot></slot>
        </div>

        <div v-if="hasFooter" class="modal-footer" ref="modalFooter" :class="footerStyle">
          <slot name="footer"> </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onBeforeUnmount, watch, useSlots, computed } from "vue";
import type { PropType } from "vue";
import { ModalStyleOptions } from "@/models/modal";

const emit = defineEmits<{
  (e: "close"): void;
}>();

const props = defineProps({
  closeBtn: {
    type: Boolean,
    default: true,
  },
  styleOptions: {
    type: Object as PropType<ModalStyleOptions>,
    required: false,
  },
  footerStyle: {
    type: String,
    default: "right",
    required: false,
  },
  closeBtnHidden: {
    type: Boolean,
    default: false,
  },
  allowClickOutsideToClose: {
    type: Boolean,
    default: false,
    required: false,
  },
});

// Template Refs
const modalMask = ref<HTMLDivElement | null>(null);
const modalWrapper = ref<HTMLDivElement | null>(null);
const modalContainer = ref<HTMLDivElement | null>(null);
const modalHeader = ref<HTMLDivElement | null>(null);
const modalTitle = ref<HTMLDivElement | null>(null);
const modalBody = ref<HTMLDivElement | null>(null);
const modalFooter = ref<HTMLDivElement | null>(null);

const slots = useSlots();

const hasFooter = computed<boolean>(() => {
  return !!slots.footer;
});

const modalStyle = computed(() => {
  return {
    "--modal-container-maxWidth": props.styleOptions?.modalContainer?.maxWidth || "800px",
    "--modal-body-minHeight": props.styleOptions?.modalBody?.minHeight || "300px",
  };
});

const windowWidth = computed<number>(() => window.innerWidth);

const mobileBreakpoint = 576;

const tabletBreakpoint = 992;

const isMobile = computed<boolean>(() => windowWidth.value <= mobileBreakpoint);

const isTablet = computed<boolean>(() => windowWidth.value <= tabletBreakpoint);

const isDesktop = computed<boolean>(() => !isMobile.value && !isTablet.value);

const useMobileSizing = computed<boolean>(() => windowWidth.value <= 480);

onBeforeMount(() => {
  // When displaying a modal we tell the body that we don't
  // want the background to scroll
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "hidden";
  }

  initializeStyle();
});

onBeforeUnmount(() => {
  // Right before we exit the modal we tell the body to perform as it did before
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "visible";
  }
});

function disableScroll() {
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "hidden";
  }
}

function enableScroll() {
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "visible";
  }
}

function initializeStyle() {
  if (props.styleOptions) {
    if (props.styleOptions?.modalContainer && modalContainer.value) {
      modalContainer.value.style.maxWidth = props.styleOptions?.modalContainer?.maxWidth || "800px";
    }

    if (props.styleOptions?.modalTitle && modalTitle.value) {
      props.styleOptions?.modalTitle?.classes?.forEach((c: string) => {
        modalTitle.value?.classList.add(c);
      });
    }

    if (props.styleOptions?.modalBody && modalBody.value) {
      modalBody.value.style.minHeight = props.styleOptions?.modalBody?.minHeight || "300px";
    }

    if (props.styleOptions?.modalTitle && modalFooter.value) {
      props.styleOptions?.modalFooter?.classes?.forEach((c: string) => {
        modalFooter.value?.classList.add(c);
      });
    }
  }
}

function handleClickOutside(): void {
  if (props.allowClickOutsideToClose) {
    emit("close");
  }
}

watch(
  () => props.styleOptions,
  () => {
    initializeStyle();
  },
  {
    deep: true,
  },
);
defineExpose({
  modalMask,
  modalWrapper,
  modalContainer,
  modalHeader,
  modalBody,
  modalTitle,
  modalFooter,
  windowWidth,
  useMobileSizing,
  isMobile,
  isDesktop,
  isTablet,
  disableScroll,
  enableScroll,
});
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 95%;
  max-width: var(--modal-container-maxWidth, 800px);
  margin: 0px auto;
  padding: 0;
  background-color: #fff;
  border-radius: 0.6rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
}

.modal-header {
  width: 100%;
  border-bottom: 0;
  padding: 0;
  border-top-left-radius: 0.3rem;
  border-top-right-radius: 0.3rem;
  display: flex;
  justify-content: space-between;
}

.modal-header:is(.keep-header) {
  padding: 1.563rem 1.5rem;
}

.modal-header .modal-title {
  margin-top: 0;
  padding: 1.563rem 1.5rem;
  width: 100%;
}

.modal-header .close-btn {
  margin: 0 0 -1rem auto;
  padding: 1rem;
  pointer-events: all;
}
.close-btn:is(.closeBtnHidden) {
  pointer-events: none;
  opacity: 0;
  display: none;
}
.modal-header .close-btn img {
  height: 18px;
  width: 18px;
}

/* .modal-header .close-btn:before {
  background: url("~@/assets/images/close-dark.png") no-repeat center;
  background-size: contain;
  display: inline-block;
  content: "";
  height: 0.894rem;
  width: 0.825rem;
  z-index: 3;
} */

.modal-body {
  margin: 0;
  padding: 0;
  overflow-y: auto;
  min-height: var(--modal-body-minHeight, "300px");
  max-height: 76vh;
}

.modal-footer {
  border-top: none;
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.modal-footer.center {
  text-align: center;
  justify-content: center;
}

.modal-footer.left {
  text-align: left;
  justify-content: flex-start;
}

.modal-footer.right {
  text-align: right;
  justify-content: flex-end;
}

.modal-footer:empty {
  height: 0;
  padding: 0;
}

.modal-footer.xs-pb-0 {
  padding-bottom: 0;
}

.modal-footer.xs-pt-0 {
  padding-top: 0;
}

/* Small and Medium devices (phones, tablets, 992px and down) */
@media (max-width: 992px) {
  .modal-body {
    max-height: 85vh;
  }

  .modal-title:empty {
    padding: 0;
  }

  .xs-mobile-w100 {
    max-width: 100% !important;
  }

  .xs-text-center {
    text-align: center;
  }
}

@media (max-width: 576px) {
  .modal-body {
    max-height: 75vh;
  }

  .xs-mobile-w100 .modal-container {
    max-width: 95% !important;
    margin: 0 auto;
  }

  .xs-mobile-h95 .modal-container {
    max-height: 95vh;
  }
}
</style>
